import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../../../shared/reducers/modals';
import {Button, Column, Image, Row, TextInput} from '../../../shared';
import debug from 'debug';
import DescriptiveText from '../../../shared/components/DescriptiveText';
import useSWR from 'swr';
import {isAuthorized} from '../../../shared/reducers/user';
import history from '../../../history';

const log = debug('circleblack:advisor:welcome');

const Welcome = ({session, openModal}) => {
  log('rendering');
  const {data: counts} = useSWR(isAuthorized(['ADVISOR', 'BRANCHMGR']) && `/crm/advisors/${session.profile.id}/counts`);
  const {data: firmManager} = useSWR(isAuthorized(['ADVISOR'])
    && (counts && !counts?.accounts?.total)
    && `/advisors/${session.profile.id}/firmmanager`);
  const {data: branchManager} = useSWR(isAuthorized(['ADVISOR'])
    && (counts && !counts?.accounts?.total)
    && `/advisors/${session.profile.id}/branchmanager`);

  let welcomeText;
  switch (session.profile.role) {
    case 'FIRMMGR':
      welcomeText = <DescriptiveText>
        Welcome to CircleBlack Advisor! As a new firm we have begun reading in account information from our
        provided feeds. For you and your advisors to begin using the platform you must first map Account Codes to Advisors so that
        we know which accounts they should have access to. Each Advisor can then take their accounts and the associated clients
        and map them into Households to allow for further information and analysis.<br/><br/>If you wish to have multiple Branches
        in our system you must map each Advisor to a Branch before they can have access.</DescriptiveText>;
      break;
    case 'BRANCHMGR':
      welcomeText = <DescriptiveText>
        Welcome to CircleBlack Advisor! As a new branch we have begun reading in account information from our
        provided feeds. For you and your advisors to begin using the platform, your Firm Manager must first map Account
        Codes to Advisors so that we know which accounts they should have access to. Each Advisor can then take their accounts and
        the associated clients and map them into Households to allow for further information and analysis.</DescriptiveText>;
      break;
    case 'ADVISOR':
    default:
      if (counts && !counts?.accounts?.total) {
        welcomeText = <DescriptiveText>
          Welcome to CircleBlack Advisor! We have begun reading in your accounts, but unfortunately we need them to
          be mapped to you by either your Firm or Branch Manager before we can display the relevant info to you. Please contact
          your Firm or Branch Manager to make sure this mapping is done so we know which accounts to make available to
          you.</DescriptiveText>;
      } else {
        welcomeText = <DescriptiveText>
          Welcome to CircleBlack Advisor! We have read in your Accounts and where possible created Clients for
          those Accounts. To get all the benefits of CircleBlack you should map all Clients into Households. A Household can be a
          group of one or more Clients and the associated Accounts for those Clients, and certain features and information only
          apply to Householded Accounts.</DescriptiveText>;
      }
  }

  return <Column>
    <Image maxWidth={'fill'} src={session.profile.firm.logo} maxHeight={'80px'} margin={'5px'}/>
    {welcomeText}
    {!!firmManager && <Row>
      <TextInput dataPoint={{name: 'name', value: firmManager.householdName}}
                 placeholder={'Firm Manager Name'}
                 title={'Firm Manager'}
                 type={'text'}
                 width={'100px'}
                 layout={{grow: 1}}
                 readOnly={true}/>
      <TextInput dataPoint={{name: 'email', value: firmManager.email}}
                 placeholder={'Firm Manager Email'}
                 title={'Email'}
                 type={'email'}
                 width={'100px'}
                 layout={{grow: 1}}
                 readOnly={true}/>
      <TextInput dataPoint={{name: 'phone', value: firmManager.phone}}
                 placeholder={'Firm Manager Phone'}
                 title={'Phone'}
                 type={'phone'}
                 width={'100px'}
                 layout={{grow: 1}}
                 readOnly={true}/>
    </Row>}
    {!!branchManager && <Row width={'100%'}>
      <TextInput dataPoint={{name: 'name', value: branchManager.name}}
                 placeholder={'Branch Manager Name'}
                 title={'Branch Manager'}
                 type={'text'}
                 width={'100px'}
                 layout={{grow: 1}}
                 readOnly={true}/>
      <TextInput dataPoint={{name: 'email', value: branchManager.email}}
                 placeholder={'Branch Manager Email'}
                 type={'email'}
                 title={'Email'}
                 width={'100px'}
                 layout={{grow: 1}}
                 readOnly={true}/>
      <TextInput dataPoint={{name: 'phone', value: branchManager.phone}}
                 placeholder={'Branch Manager Phone'}
                 type={'phone'}
                 title={'Phone'}
                 width={'100px'}
                 layout={{grow: 1}}
                 readOnly={true}/>
    </Row>}
    <Row>
      {isAuthorized(['FIRMMGR'])
      && <Button label={'Setup Branches'} importance={'secondary'} size={'md'} action={() => {
        history.push('/manage/branches');
        openModal({
          type: 'MessageModal',
          body: <DescriptiveText>Across {session.profile.brand.advisorPlatformName} your main navigation is always accessible from
            the top of the screen. Under the Manage section you can setup everything from Branches to Advisors to Account Codes and
            create mappings between them to keep everything organized and make sure your Advisors see their correct data when they
            log in.<br/><br/>You can start here by creating your Branches, then continue to the Advisors page to map
            them.</DescriptiveText>,
          title: 'Onboarding',
          button: {text: 'Continue'},
        });
      }}/>}
      {isAuthorized(['FIRMMGR'])
      && <Button label={'Setup Advisors'} importance={'secondary'} size={'md'} action={() => {
        history.push('/manage/advisors');
        openModal({
          type: 'MessageModal',
          body: <DescriptiveText>Across {session.profile.brand.advisorPlatformName} your main navigation is always accessible from
            the top of the screen. Under the Manage section you can setup everything from Advisors to Account Codes and create
            mappings between them to keep everything organized and make sure your Advisors see their correct data when they log
            in.<br/><br/>You can start here by setting up your Advisors, then continue to the Account Codes page to map them to the
            correct Advisors.</DescriptiveText>,
          title: 'Onboarding',
          button: {text: 'Continue'},
        });
      }}/>}
      {isAuthorized(['ADVISOR', 'BRANCHMGR']) && !!counts?.clients?.total
      && <Button label={'Manage Clients'} importance={'primary'} size={'md'} action={() => {
        history.push('/manage/clients');
        openModal({
          type: 'MessageModal',
          body: <DescriptiveText>Across {session.profile.brand.advisorPlatformName}your main navigation is always accessible from
            the top of the screen. Under the Manage section you can setup everything for your Clients, from making sure Accounts are
            properly mapped to them to organizing them into Households.<br/><br/>You can start here by householding your clients to
            begin utilizing our deep analytics system.</DescriptiveText>,
          title: 'Onboarding',
          button: {text: 'Continue'},
        });
      }}/>}
    </Row>
  </Column>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Welcome);

Welcome.propTypes = {
  accountID: PropTypes.number.isRequired,
  advisorID: PropTypes.number,
  session: PropTypes.object,
  openModal: PropTypes.func.isRequired,
  updateContext: PropTypes.func,
};
